'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { Alert, Box, Button, CircularProgress } from '@mui/material'

import { selectAuth } from '@/src/redux/slices/authSlice'
import { useAppSelector } from '@/src/redux/store'
import { useGetAccountProfileQuery } from '@/src/api/rtkQuery/profileApi'
import useFormatDate from '@/src/hooks/date/useFormatDate'
import { transformProfileResponse } from '@/src/utils/profile/transformProfileResponse'
import { buildTravellerDisplay } from '@/src/utils/travellers/buildTravellerDisplay'
import { isCleartrip } from '@/src/utils/generalUtils/isCleartrip'

import { MobileProfileView } from './profile'
import useRedirectToHome from '@/src/hooks/navigation/useRedirectToHome'

const SignInModalWrapper = dynamic(
    () => import(/* webpackChunkName: "signin-modal" */ '../../common/header/variants/flyin/SignInModal/SignInModalWrapper'),
    { ssr: false }
)

const MobileProfilePageWrapper: React.FC = () => {
    const t = useTranslations('account.profile')
    const locale = useLocale()
    const isRtl = locale === 'ar'
    const router = useRouter()
    const goToHome = useRedirectToHome()
    const authState = useAppSelector(selectAuth)
    const isLoggedIn = authState.signedIn
    const { formatDate } = useFormatDate()
    const { data: rawProfile, isLoading, isError, refetch } = useGetAccountProfileQuery({
        fetchControl: isCleartrip() ? 'MAIN' : 'ALL'
    })

    const [isMounted, setIsMounted] = useState(false)
    const [isSignInOpen, setIsSignInOpen] = useState(false)

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setIsMounted(true)
        }
    }, [])

    useEffect(() => {
        if (isMounted && !isLoggedIn) {
            setIsSignInOpen(true)
        }
    }, [isMounted, isLoggedIn])

    const handleSignInClose = useCallback(() => {
        setIsSignInOpen(false)
        if (!isLoggedIn) {
            goToHome({ isReloadRequired: false })
        }
    }, [isLoggedIn, goToHome])

    const profileForView = useMemo(() => {
        if (!rawProfile) return null
        return transformProfileResponse(rawProfile)
    }, [rawProfile])

    const derived = useMemo(() => {
        if (!profileForView) return null
        const { displayName: fullName, initials } = buildTravellerDisplay({
            title: profileForView.title,
            firstName: profileForView.firstName,
            lastName: profileForView.lastName
        })

        const phoneLabel = profileForView.phone?.number ? `+${profileForView.phone.countryCode} ${profileForView.phone.number}` : '--'
        const dateOfBirthValue = profileForView.dateOfBirth ? formatDate(profileForView.dateOfBirth, 'DD MMMM YYYY') : '--'

        return {
            fullName,
            initials,
            email: profileForView.email,
            phoneLabel,
            gender: profileForView.gender,
            screenName: profileForView.screenName || '--',
            dateOfBirthValue,
            addressLabel: profileForView.addressLabel || '--'
        }
    }, [profileForView, formatDate])

    const onBack = useCallback(() => {
        goToHome({ isReloadRequired: false })
    }, [])

    const renderContent = () => {
        if (isLoading) {
            return (
                <Box sx={{ minHeight: '100dvh', display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            )
        }

        if ((isError || !derived) && !isLoading) {
            return (
                <Box sx={{ px: 2, pt: 2 }}>
                    <Alert
                        severity="error"
                        sx={{ borderRadius: 2, border: 1, borderColor: 'divider' }}
                        action={
                            <Button color="inherit" size="small" onClick={refetch} sx={{ textTransform: 'none', fontWeight: 600 }}>
                                {t('retry')}
                            </Button>
                        }
                    >
                        {t('failedToLoad')}
                    </Alert>
                </Box>
            )
        }

    const resolvedProfile = profileForView
    const resolvedDerived = derived

    if (!resolvedProfile || !resolvedDerived) {
        return null
    }

        return (
            <MobileProfileView
                t={t}
                profile={profileForView}
                fullName={derived.fullName}
                initials={derived.initials}
                phoneLabel={derived.phoneLabel}
                dateOfBirthValue={derived.dateOfBirthValue}
                onBack={onBack}
                isRtl={isRtl}
            />
        )
    }

    return (
        <>
            {renderContent()}
            {isSignInOpen && <SignInModalWrapper open={isSignInOpen} onClose={handleSignInClose} />}
        </>
    )
}

export { MobileProfilePageWrapper }
export default MobileProfilePageWrapper
